import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  GripVertical,
  Palette,
  EyeOff,
  Check,
  AlertCircle,
} from "lucide-react";
import { useEditorStore } from "../../../../store/editorStore";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { SkinThumbnail } from "../../../../components/SkinThumbnail";
import { spineStaticThumbUrl } from "commons/cdn";

import { SKU } from "../../../../types/sku";

// 恢复原本的 SortableSkuItemProps，移除 isRelated 和 spineData
export interface SortableSkuItemProps {
  sku: SKU;
  selectionId: string;
  isSelected: boolean;
  isEditing: boolean;
  category: string; // SKU 所属的 category
  hasConflict: boolean; // 是否与当前选中的 skin 有 category 冲突
  pro?: number; // 从 skus 获取的价格
  hidden?: boolean; // 从 skus 获取的隐藏状态
  toningIds?: string[]; // 从 skus 获取的染色绑定
  onToggle: () => void;
  onEdit: () => void;
}

export function SortableSkuItem({
  sku,
  selectionId: _selectionId,
  isSelected,
  isEditing,
  category,
  hasConflict,
  pro,
  hidden,
  toningIds,
  onToggle,
  onEdit,
}: SortableSkuItemProps) {
  void _selectionId; // Suppress unused variable warning

  // 从 store 获取 SKU 错误（集中校验）
  const getSkuErrors = useEditorStore((s) => s.getSkuErrors);
  const skuErrors = getSkuErrors(sku.id);

  // 从错误中提取状态
  const isOrphan = skuErrors.some((e) => e.type === "orphan");
  const hasDataError = skuErrors.some(
    (e) => e.type === "invalid_toning" || e.type === "duplicate_toning",
  );
  const errorMessages = skuErrors.map((e) => e.message).join("; ");
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: sku.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleClick = () => {
    onToggle();
    onEdit();
  };

  const hasError = isOrphan || hasDataError;

  const customThumbUrl = sku.thumbAssetId
    ? spineStaticThumbUrl(sku.thumbAssetId)
    : "";

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "relative rounded-lg border p-1.5 transition-colors",
        hasError
          ? "border-red-500 border-2 bg-red-50 dark:bg-red-950/20"
          : isEditing
            ? "border-primary ring-2 ring-primary bg-primary/10"
            : "bg-muted/30 hover:bg-muted/50",
        isDragging && "opacity-50 shadow-lg z-10",
      )}
      title={
        isOrphan
          ? "Skin 已不存在，请删除或更新"
          : hasDataError
            ? `⚠️ ${errorMessages}`
            : undefined
      }
    >
      {/* 拖拽手柄 */}
      <div
        {...attributes}
        {...listeners}
        className="absolute top-0.5 left-0.5 h-5 w-5 flex items-center justify-center cursor-grab"
      >
        <GripVertical className="h-3 w-3 text-muted-foreground" />
      </div>
      {/* 可点击区域 */}
      <div className="cursor-pointer" onClick={handleClick}>
        {/* 缩略图：有自定义封面优先 CDN，否则从 ZIP 渲染 */}
        <div className="flex justify-center mb-1">
          {customThumbUrl ? (
            <img
              src={customThumbUrl}
              alt=""
              className="w-14 h-14 object-contain rounded border bg-muted/30"
            />
          ) : (
            <SkinThumbnail skinName={sku.skinName} size={56} />
          )}
        </div>
        {/* Category + SKU ID */}
        <div className="text-center space-y-0.5">
          <span
            className={cn(
              "truncate block text-[10px]",
              hasConflict && !isSelected
                ? "text-amber-600 font-medium"
                : "text-muted-foreground",
            )}
            title={
              hasConflict && !isSelected
                ? `与当前选中的 skin 同属 "${category}"，选择后会替换`
                : category
            }
          >
            {category}
          </span>
          <span className="truncate block text-[9px] font-mono text-muted-foreground/60">
            {sku.id}
          </span>
        </div>
        {/* 价格、染色状态、隐藏标记、错误提示 */}
        <div className="flex items-center justify-center gap-1 mt-0.5">
          <Badge
            variant={(pro || 0) > 0 ? "default" : "secondary"}
            className={cn(
              "text-[10px] px-1.5 h-4",
              (pro || 0) > 0 ? "bg-amber-500 hover:bg-amber-600" : "",
            )}
          >
            {(pro || 0) > 0 ? `¥${pro}` : "免费"}
          </Badge>
          {toningIds && toningIds.length > 0 && (
            <div
              className="h-4 min-w-4 px-0.5 rounded flex items-center justify-center gap-0.5 bg-purple-100"
              title={`已绑定 ${toningIds.length} 个染色方案`}
            >
              <Palette className="h-2.5 w-2.5 text-purple-600" />
              {toningIds.length > 1 && (
                <span className="text-[9px] font-medium text-purple-600">
                  {toningIds.length}
                </span>
              )}
            </div>
          )}
          {/* 隐藏标记 */}
          {hidden && (
            <div
              className="h-4 w-4 rounded flex items-center justify-center bg-zinc-200"
              title="App 中隐藏"
            >
              <EyeOff className="h-2.5 w-2.5 text-zinc-600" />
            </div>
          )}
          {/* 数据错误标记 */}
          {hasDataError && (
            <div
              className="h-4 w-4 rounded flex items-center justify-center bg-red-500"
              title={`⚠️ ${errorMessages}`}
            >
              <AlertCircle className="h-2.5 w-2.5 text-white" />
            </div>
          )}
        </div>
      </div>
      {/* 装载态打勾 - 右上角 */}
      {isSelected && !isOrphan && (
        <div className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-emerald-500 flex items-center justify-center">
          <Check className="h-3 w-3 text-white" strokeWidth={3} />
        </div>
      )}
    </div>
  );
}
